import type { MaterialSummary } from './api';
import { MAX_FILES_PER_ADD, clientRefusal, defaultTitle } from './upload-copy';

/**
 * The upload panel's queue: a pure reducer, and the two questions the runner asks of it.
 *
 * Nothing here touches a file's bytes, the network or the clock. Ids and times arrive in the
 * events; the runner (`use-upload-queue.ts`) is the imperative shell that turns a request's
 * progress and its answer into events. That split is what lets every transition below be
 * tested by feeding events in, and what lets a property test throw random sequences at it.
 *
 *     queued → uploading → checking → thumbnail → done
 *                  ↘          ↘          (never cancelled, never an error)
 *                   error(reason) | cancelled
 *
 * - `checking` is the gap between the last byte sent and the Worker's reply — when the Office
 *   inspection runs.
 * - `thumbnail` is the thumbnail step. With no thumbnail maker it is skipped, and a thumbnail that
 *   fails still lands on `done`: the material is in the group whatever happened to its picture.
 * - A 429 sends the file back to `queued` with a `retryAt`, once. The second is an error.
 *
 * Two invariants the property test holds this to: an item is in exactly one state, and it is
 * `done` only after an `ok` (a 2xx) arrived for it — `material` is set by that event and by
 * nothing else.
 */

export type UploadState =
  | 'queued' | 'uploading' | 'checking' | 'thumbnail' | 'done' | 'error' | 'cancelled';

export interface FileMeta {
  /** Minted by the runner. The reducer never invents one. */
  id: string;
  name: string;
  size: number;
  type: string;
}

export interface UploadItem extends FileMeta {
  groupId: string;
  title: string;
  note: string;
  state: UploadState;
  progress: { sent: number; total: number };
  /** The refusal reason, keyed like `UPLOAD_REFUSAL_COPY`. Set only in `error`. */
  error: string | null;
  /** What the Worker wrote. Set by `ok` and nothing else. */
  material: MaterialSummary | null;
  /** When a rate-limited file may go again (ms since epoch). */
  retryAt: number | null;
  /** Whether its one retry has been spent. */
  retried: boolean;
  /** A request is out for it. An `ok` may still arrive after a cancel, and it is the truth. */
  sending: boolean;
}

export interface QueueState {
  items: UploadItem[];
}

export type QueueEvent =
  | { type: 'add'; groupId: string; files: FileMeta[] }
  | { type: 'edit'; id: string; title?: string; note?: string }
  | { type: 'start'; id: string }
  | { type: 'progress'; id: string; sent: number; total: number }
  | { type: 'sent'; id: string }
  | { type: 'ok'; id: string; material: MaterialSummary; thumbnail: boolean }
  | { type: 'refused'; id: string; reason: string }
  | { type: 'network_error'; id: string }
  | { type: 'rate_limited'; id: string; retryAt: number }
  | { type: 'cancel'; id: string }
  | { type: 'thumbnail_done'; id: string }
  | { type: 'thumbnail_failed'; id: string }
  | { type: 'dismiss' };

export const EMPTY_QUEUE: QueueState = { items: [] };

const ACTIVE: ReadonlySet<UploadState> = new Set(['queued', 'uploading', 'checking', 'thumbnail']);
const SENDING: ReadonlySet<UploadState> = new Set(['uploading', 'checking']);
const CANCELLABLE: ReadonlySet<UploadState> = new Set(['queued', 'uploading', 'checking']);

export const isActive = (item: UploadItem): boolean => ACTIVE.has(item.state);
export const canCancel = (item: UploadItem): boolean => CANCELLABLE.has(item.state);
export const canEdit = (item: UploadItem): boolean => item.state === 'queued';

/** The title that is sent: what the teacher typed, or the filename if they emptied it. */
export const titleFor = (item: Pick<UploadItem, 'title' | 'name'>): string =>
  item.title.trim() || defaultTitle(item.name);

function newItem(groupId: string, file: FileMeta, overCap: boolean): UploadItem {
  const refusal = overCap ? 'too_many_files' : clientRefusal(file);
  return {
    ...file,
    groupId,
    title: defaultTitle(file.name),
    note: '',
    state: refusal ? 'error' : 'queued',
    progress: { sent: 0, total: file.size },
    error: refusal,
    material: null,
    retryAt: null,
    retried: false,
    sending: false,
  };
}

/** Change one item, if `when` holds for it. Anything else leaves the state as it was. */
function patch(
  state: QueueState,
  id: string,
  when: (item: UploadItem) => boolean,
  change: (item: UploadItem) => Partial<UploadItem>,
): QueueState {
  let changed = false;
  const items = state.items.map((item) => {
    if (item.id !== id || !when(item)) return item;
    changed = true;
    return { ...item, ...change(item) };
  });
  return changed ? { items } : state;
}

const sending = (item: UploadItem) => SENDING.has(item.state);

export function reduceQueue(state: QueueState, event: QueueEvent): QueueState {
  switch (event.type) {
    case 'add': {
      // The first ten of one drop are taken; the rest get a row that says why they were not,
      // so a teacher can see exactly which files to add again.
      const known = new Set(state.items.map((item) => item.id));
      const fresh = event.files.filter((file) => !known.has(file.id));
      if (fresh.length === 0) return state;
      return {
        items: [
          ...state.items,
          ...fresh.map((file, index) => newItem(event.groupId, file, index >= MAX_FILES_PER_ADD)),
        ],
      };
    }

    case 'edit':
      return patch(state, event.id, canEdit, () => ({
        ...(event.title !== undefined ? { title: event.title } : {}),
        ...(event.note !== undefined ? { note: event.note } : {}),
      }));

    case 'start':
      return patch(state, event.id, canEdit, (item) => ({
        state: 'uploading',
        retryAt: null,
        sending: true,
        progress: { sent: 0, total: item.size },
      }));

    case 'progress':
      return patch(state, event.id, (item) => item.state === 'uploading', () => ({
        progress: { sent: Math.max(0, Math.min(event.sent, event.total)), total: event.total },
      }));

    case 'sent':
      return patch(state, event.id, (item) => item.state === 'uploading', (item) => ({
        state: 'checking',
        progress: { sent: item.progress.total, total: item.progress.total },
      }));

    case 'ok':
      // A cancel that lands after the Worker already took the file cannot take it back. The
      // row says what is true — it is in the group — rather than "Cancelled".
      return patch(
        state,
        event.id,
        (item) => sending(item) || (item.state === 'cancelled' && item.sending),
        (item) => ({
          state: event.thumbnail ? 'thumbnail' : 'done',
          material: event.material,
          sending: false,
          error: null,
          progress: { sent: item.progress.total, total: item.progress.total },
        }),
      );

    case 'refused':
    case 'network_error':
      return patch(state, event.id, sending, () => ({
        state: 'error', error: event.type === 'refused' ? event.reason : 'network', sending: false,
      }));

    case 'rate_limited':
      return patch(state, event.id, sending, (item) => (item.retried
        ? { state: 'error', error: 'rate_limited', sending: false }
        : {
          state: 'queued',
          retryAt: event.retryAt,
          retried: true,
          sending: false,
          progress: { sent: 0, total: item.size },
        }));

    case 'cancel':
      // Not in `thumbnail`: the material is already in the group by then.
      return patch(state, event.id, canCancel, () => ({ state: 'cancelled', retryAt: null }));

    case 'thumbnail_done':
    case 'thumbnail_failed':
      // Either way the material is there. A missing picture is never an error.
      return patch(state, event.id, (item) => item.state === 'thumbnail', () => ({ state: 'done' }));

    case 'dismiss':
      return state.items.some((item) => !isActive(item))
        ? { items: state.items.filter(isActive) }
        : state;
  }
}

/**
 * What the runner should do next. One file at a time, in the order they were added: nothing
 * starts while another is uploading or being checked, and a rate-limited file at the head
 * holds the line until its `retryAt` rather than letting the next file walk into the same 429.
 */
export type NextStep =
  | { kind: 'idle' }
  | { kind: 'start'; item: UploadItem }
  | { kind: 'wait'; ms: number };

export function nextStep(state: QueueState, now: number): NextStep {
  if (state.items.some(sending)) return { kind: 'idle' };
  const head = state.items.find((item) => item.state === 'queued');
  if (!head) return { kind: 'idle' };
  if (head.retryAt !== null && head.retryAt > now) return { kind: 'wait', ms: head.retryAt - now };
  return { kind: 'start', item: head };
}

/** What the panel's header says. */
export function queueHeadline(items: readonly UploadItem[]): string {
  const total = items.length;
  const active = items.filter(isActive).length;
  if (active > 0) {
    const position = Math.min(total, total - active + 1);
    return `Uploading ${position} of ${total}`;
  }
  const done = items.filter((item) => item.state === 'done').length;
  const files = (n: number) => (n === 1 ? 'file' : 'files');
  if (done === total) return `Uploaded ${total} ${files(total)}`;
  return `Uploaded ${done} of ${total} ${files(total)}`;
}
